import React from 'react'

import {omit} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'
import * as Yup from 'yup'

import {useAppSelector} from '../../hooks/redux'

import useAsync from '../../../../commons/src/hooks/use-async'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'
import * as providerCertificationApi from '../../services/api/provider-certification'
import {captureException} from '../../services/logging'
import {toTouchedFormData} from '../utils/form'

import {ProviderCertificationOption} from '../../../../commons/src/types'
import {ProviderId} from '../../types'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Button from '../../../../commons/src/components/button'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import LabeledCheckbox from '../../../../commons/src/components/labeled-checkbox-field'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import Section from '../../../../commons/src/components/section'
import Stack from '../../../../commons/src/components/stack'

export const certificationOptions: {id: ProviderCertificationOption; name: string}[] = [
  {id: 'iso_9001', name: 'ISO 9001'},
  {id: 'as_9100', name: 'AS 9100'},
  {id: 'iso_13485', name: 'ISO 13485'},
  {id: 'iso_14001', name: 'ISO 14001'},
  {id: 'iso_ts16949', name: 'ISO TS16949'},
  {id: 'iso_52920', name: 'ISO 52920'},
]

const initialCertificationValues: Record<ProviderCertificationOption, boolean> = {
  iso_9001: false,
  as_9100: false,
  iso_13485: false,
  iso_14001: false,
  iso_ts16949: false,
  iso_52920: false,
}

type Props = {
  providerId: ProviderId
}

const SettingsProfileCertificationsPartial = ({providerId}: Props) => {
  const [isProviderCertificationsSubmitted, setIsProviderCertificationsSubmitted] =
    React.useState(false)

  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const {
    isLoading: isLoadingProviderCertifications,
    data: providerCertifications = [],
    refresh: receiveProviderCertifications,
    error: providerCertificationError,
  } = useAsync(() => providerCertificationApi.getByProvider(provider.id), [], {
    onError: error => {
      captureException(error)
    },
  })

  const memoizedInitialSpecificationValues = React.useMemo(
    () => ({
      ...initialCertificationValues,
      ...providerCertifications.reduce(
        (acc, certification) => {
          acc[certification.certification] = true
          return acc
        },
        {} as Record<ProviderCertificationOption, boolean>
      ),
      formikGeneral: '',
    }),
    [providerCertifications]
  )

  const providerCertificationsFormik = useFormik<
    Record<ProviderCertificationOption, boolean> & {formikGeneral: string}
  >({
    enableReinitialize: true,
    initialValues: {
      ...initialCertificationValues,
      ...providerCertifications.reduce(
        (acc, certification) => {
          acc[certification.certification] = true
          return acc
        },
        {} as Record<ProviderCertificationOption, boolean>
      ),
      formikGeneral: '',
    },
    validationSchema: Yup.object({
      ...Object.keys(initialCertificationValues).reduce(
        (acc, key) => {
          acc[key] = Yup.boolean().required()
          return acc
        },
        {} as Record<string, Yup.Schema<boolean>>
      ),
    }),
    onSubmit: async (values, actions) => {
      const touchedValues = omit(toTouchedFormData(values, memoizedInitialSpecificationValues), [
        'formikGeneral',
      ]) as Record<string, boolean>

      const idsToDelete = providerCertifications
        .filter(item => touchedValues[item.certification] === false)
        .map(item => item.id)

      const CertificationsToCreate = Object.keys(touchedValues)
        .filter(key => touchedValues[key])
        .map(key => ({
          certification: key as ProviderCertificationOption,
          provider: providerId,
        }))

      try {
        await Promise.all([
          providerCertificationApi.removeMultiple(idsToDelete),
          providerCertificationApi.createMultiple(CertificationsToCreate),
        ])

        setIsProviderCertificationsSubmitted(true)
        receiveProviderCertifications()
      } catch (error) {
        captureException(error)
        actions.setFieldError('formikGeneral', `Update failed with: ${(error as Error).message}`)
      }
    },
  })

  return (
    <Section>
      <Stack>
        <Headline label="Certifications" className="u-no-margin-bottom" />
        <Paragraph className="u-no-margin-bottom">
          Select the certifications that apply to your facility and share all certifications with
          the Partner Management team.
        </Paragraph>
        {isLoadingProviderCertifications && (
          <div>
            <LoadingIndicator />
          </div>
        )}
        {!isLoadingProviderCertifications && providerCertificationError && (
          <ActionBar>
            <Paragraph size="xs" warning className="u-no-margin-bottom">
              Loading your current certification settings failed
            </Paragraph>
            <LinkButton
              icon={<Icon source={refreshIcon} />}
              onClick={() => {
                receiveProviderCertifications()
              }}
            />
          </ActionBar>
        )}
        {!isLoadingProviderCertifications && !providerCertificationError && (
          <form onSubmit={providerCertificationsFormik.handleSubmit}>
            {certificationOptions.map(certification => (
              <LabeledCheckbox
                key={certification.id}
                name={certification.id}
                label={certification.name}
                checked={providerCertificationsFormik.values[certification.id]}
                appearance={
                  providerCertificationsFormik.submitCount > 0 &&
                  !!providerCertificationsFormik.errors[certification.id]
                    ? 'error'
                    : undefined
                }
                onChange={(value, name) => {
                  providerCertificationsFormik.setFieldValue(name as string, value)
                  if (isProviderCertificationsSubmitted) {
                    setIsProviderCertificationsSubmitted(false)
                  }
                }}
                onBlur={providerCertificationsFormik.handleBlur}
              />
            ))}
            {providerCertificationsFormik.errors.formikGeneral && (
              <Paragraph error>{providerCertificationsFormik.errors.formikGeneral}</Paragraph>
            )}
          </form>
        )}
        <ActionBar>
          <Button
            disabled={
              providerCertificationsFormik.isSubmitting ||
              isProviderCertificationsSubmitted ||
              !providerCertificationsFormik.dirty
            }
            icon={
              isProviderCertificationsSubmitted ? (
                <Icon source={checkedIcon} />
              ) : providerCertificationsFormik.isSubmitting &&
                providerCertificationsFormik.submitCount > 0 &&
                isEmpty(providerCertificationsFormik.errors) ? (
                <LoadingIndicator invert />
              ) : undefined
            }
            onClick={preventDoubleClick(() => providerCertificationsFormik.handleSubmit())}
          >
            {isProviderCertificationsSubmitted ? 'Saved' : 'Save'}
          </Button>
          <Button
            hierarchy="secondary"
            disabled={
              providerCertificationsFormik.isSubmitting ||
              isProviderCertificationsSubmitted ||
              !providerCertificationsFormik.dirty
            }
            onClick={preventDoubleClick(() => {
              providerCertificationsFormik.resetForm()
            })}
          >
            Reset
          </Button>
        </ActionBar>
      </Stack>
    </Section>
  )
}

export default SettingsProfileCertificationsPartial
